/**
 * RED-318 — column layout of one price-statistics result, shared by the drawer
 * table and the copied (TSV) text so both show the same cells under the same
 * labels. It follows the result's `columns` (cells shown, count first) and
 * `table` (`area_unit` m2 | ha, `measure` price_per_m2 | price_per_ha | sum):
 * forest and arable land show hectares and €/ha, and hoonestatud maa has no
 * unit-price group — its median / mean / SD are of the deal sum and sit under
 * "Tehingu summa".
 */
import { DECIMALS } from '@/services/numberFormat'

/** Every contract cell in table order (results cached before RED-318 carry no `columns`). */
export const DEFAULT_COLUMNS = Object.freeze([
  'count',
  'area_mean',
  'sum_total',
  'sum_min',
  'sum_max',
  'ppm_min',
  'ppm_max',
  'ppm_median',
  'ppm_mean',
  'ppm_sd',
])

const UNIT_LABEL = { m2: 'm²', ha: 'ha', price_per_m2: 'm²', price_per_ha: 'ha' }

/** i18n leaf key under `messages.transactions.analytics.table`. */
const LEAF_KEY = {
  sum_total: 'total',
  sum_min: 'min',
  sum_max: 'max',
  ppm_min: 'min',
  ppm_max: 'max',
  ppm_median: 'median',
  ppm_mean: 'mean',
  ppm_sd: 'sd',
}

/** Copy header suffix after the group label (Estonian, design-spec §5.1). */
const TSV_SUFFIX = {
  sum_total: 'kokku',
  sum_min: 'min',
  sum_max: 'max',
  ppm_min: 'min',
  ppm_max: 'max',
  ppm_median: 'mediaan',
  ppm_mean: 'keskmine',
  ppm_sd: 'standardhälve',
}

/**
 * RED-370 — how every price-statistics table in the Analüütika drawer (band,
 * period comparison, per region) fits its 1040 px. Quicksand is never loaded,
 * so the tables set in the platform's sans: Linux's DejaVu Sans (CI) runs
 * about 90 px wider than macOS's on three counties' nine-digit sums, and at
 * 16 px the band table nested in a region's row ran 46 px past the drawer
 * there. Each table's `text-sm` (14 px), 6 px cell sides instead of PrimeVue's
 * small 8 px, the bare expansion cell below and the soft hyphen in et.json's
 * "Standard­hälve" (the header may break as "Standard-/hälve") are what
 * leave every table 64 px or more to spare on Linux; keep all four. Spec 52
 * holds each table to 24 px of headroom on the platform that runs it.
 */
const COMPACT_PADDING = { sm: { padding: '0.375rem' } }
export const COMPACT_TABLE_DT = Object.freeze({
  headerCell: COMPACT_PADDING,
  bodyCell: COMPACT_PADDING,
  footerCell: COMPACT_PADDING,
})

/** An expanded row's cell gets no padding of its own: its `p-2` band already frames the nested table. */
export const EXPANSION_PT = Object.freeze({ rowExpansionCell: { class: 'p-0' } })

/**
 * @param {object|null} table the result's `table`
 * @param {string[]|null} columns the result's `columns`
 */
export const tableLayout = (table, columns) => {
  const cells = columns?.length ? columns : DEFAULT_COLUMNS
  const unitPrice = (table?.measure ?? 'price_per_m2') !== 'sum'
  const areaUnit = UNIT_LABEL[table?.area_unit] ?? 'm²'
  const priceUnit = UNIT_LABEL[table?.measure] ?? 'm²'

  const valueColumns = cells
    .filter(field => field !== 'count')
    .map(field => ({
      field,
      dp: field.startsWith('ppm_') && !unitPrice ? 0 : (DECIMALS[field] ?? 0),
      group: field === 'area_mean' ? null : field.startsWith('sum_') || !unitPrice ? 'sum' : 'unit',
      leafKey: LEAF_KEY[field] ?? null,
    }))

  const tsvLabel = column => {
    if (column.field === 'area_mean') return `Pindala keskmine (${areaUnit})`
    const group = column.group === 'unit' ? `Pinnaühiku hind (eur/${priceUnit})` : 'Tehingu summa (eur)'
    return `${group} ${TSV_SUFFIX[column.field]}`
  }

  return {
    areaUnit,
    priceUnit,
    valueColumns,
    /** Leaf columns under the two header groups (everything but the area mean). */
    groupedColumns: valueColumns.filter(column => column.group !== null),
    sumSpan: valueColumns.filter(column => column.group === 'sum').length,
    unitSpan: valueColumns.filter(column => column.group === 'unit').length,
    /** @param {string} groupHeader first cell (the row axis, e.g. "Pindala (m²)") */
    tsvHeader: groupHeader => [groupHeader, 'Arv', ...valueColumns.map(tsvLabel)],
  }
}
